import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProviderAuthModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [sheetBottom, setSheetBottom] = useState(0);

  // Lock body scroll when open
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Keyboard-aware positioning via visualViewport — mobile only
  // No transforms involved: solo ajustamos `bottom` con state de React.
  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.visualViewport) return;

    const onViewportChange = () => {
      // keyboardHeight = la diferencia entre la ventana y el viewport visible
      const kb = Math.max(0, window.innerHeight - window.visualViewport.height);
      setSheetBottom(kb);
    };

    window.visualViewport.addEventListener('resize', onViewportChange);
    window.visualViewport.addEventListener('scroll', onViewportChange);
    onViewportChange(); // calcular al abrir

    return () => {
      window.visualViewport.removeEventListener('resize', onViewportChange);
      window.visualViewport.removeEventListener('scroll', onViewportChange);
      setSheetBottom(0);
    };
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/providers/login`, {
        email: form.email,
        password: form.password,
      });
      localStorage.setItem('provider_token', data.token);
      localStorage.setItem('provider_data', JSON.stringify(data.provider));
      toast.success('¡Bienvenido!');
      onSuccess(data.token, data.provider);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const isMobile = window.innerWidth < 640;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      {/*
        MOBILE (<640px): bottom sheet sin transforms.
        La posición se controla únicamente con `bottom` (state de React).
        Cero conflictos con Radix ni con animaciones CSS.

        DESKTOP (≥640px): modal centrado con transform normal (el teclado
        virtual no afecta el viewport en desktop).
      */}
      <div
        data-testid="auth-modal"
        className={
          isMobile
            ? 'absolute left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[88vh] overflow-y-auto'
            : 'absolute left-1/2 top-1/2 w-full max-w-md bg-white rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto'
        }
        style={
          isMobile
            ? {
                bottom: sheetBottom,
                paddingBottom: `calc(1.5rem + env(safe-area-inset-bottom, 0px))`,
              }
            : {
                transform: 'translate(-50%, -50%)',
              }
        }
      >
        {/* Contenido del panel */}
        <div className="px-6 pt-5 pb-2">
          {/* Drag handle — solo mobile */}
          {isMobile && (
            <div className="flex justify-center mb-4">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
          )}

          {/* Encabezado */}
          <div className="mb-5">
            <h2 className="text-xl font-semibold text-gray-900">
              Iniciar sesión
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Ingresá tu cuenta para ver los datos de contacto
            </p>
          </div>

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="tu@email.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">Contraseña</label>
              <div className="relative">
                <input
                  data-testid="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="tu contraseña"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  minLength={8}
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
            </div>

            {/* Submit */}
            <button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold text-white transition-opacity disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
              style={{ backgroundColor: 'var(--primary-strong)' }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : 'Iniciar sesión'}
            </button>
          </form>

        </div>
      </div>
    </div>,
    document.body
  );
}
